import { useEffect, useMemo, useState, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { DataGrid } from "../../Controls/DataGrid";
import { Plus, Pencil, PencilFill, Trash, Files } from "react-bootstrap-icons";
import { PageBar, ToolButton } from "../../Controls/PageBar";
import { useAtom } from "jotai";
import { createApiService } from "../../shared/apiConfig";
import { FeesApi, ServerRequestsFeeInfo, OperatorApi } from "../../shared/api/axios-client";
import { showMoney, cents } from "../../Data/Money";
import { CSSTransition } from "react-transition-group";
import { FeeEditModal } from "./FeeEditModal";
import { TestFacilityModal } from "./TestFacilityModal";
import { ConfirmModal } from "../../Controls/ConfirmModal";
import { Select } from "../../Controls/Select";

export function AdminFeesListPage() {
    
    const [feesData, setFeesData] = useState<ServerRequestsFeeInfo[]>([]);
    const [operators, setOperators] = useState<any[]>([]);
    const [showContent, setShowContent] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFee, setSelectedFee] = useState<ServerRequestsFeeInfo | null>(null);
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState<string>("");
    const [selectedFacility, setSelectedFacility] = useState<string>("");
    const nodeRef = useRef(null);
    const feesService = createApiService(FeesApi);
    const operatorService = createApiService(OperatorApi);

    // Create dropdown options from unique values in the data
    const operatorOptions = useMemo(() => {
        const uniqueOperators = [...new Set(feesData.map(fee => fee.operatorName).filter(Boolean))];
        return [
            { value: "", label: "All Operators" },
            ...uniqueOperators.map(operator => ({ value: operator, label: operator }))
        ];
    }, [feesData]);

    const facilityOptions = useMemo(() => {
        // Filter facilities based on selected operator
        const relevantFees = selectedOperator 
            ? feesData.filter(fee => fee.operatorName === selectedOperator)
            : feesData;
        
        const uniqueFacilities = [...new Set(relevantFees.map(fee => fee.facilityName).filter(Boolean))];
        return [
            { value: "", label: "All Facilities" },
            ...uniqueFacilities.map(facility => ({ value: facility, label: facility }))
        ];
    }, [feesData, selectedOperator]);

    // Filter the data based on selected filters
    const filteredFeesData = useMemo(() => {
        return feesData.filter(fee => {
            const operatorMatch = !selectedOperator || fee.operatorName === selectedOperator;
            const facilityMatch = !selectedFacility || fee.facilityName === selectedFacility;
            return operatorMatch && facilityMatch;
        });
    }, [feesData, selectedOperator, selectedFacility]);

   
    const getFeeScopeText = (scope: number) => {
        switch (scope) {
            case 0: return "Reservation";
            case 1: return "Subscription";
            default: return "Unknown";
        }
    };

    const getFeeCalcText = (calc: number) => {
        switch (calc) {          
            case 0: return "Fixed";
            case 1: return "Percent";
            default: return "Unknown";
        }
    };

    const getPaymentTypeText = (type: number) => {
        switch (type) {
            case 0: return "Credit Card";
            case 1: return "ACH";
            case 2: return "Any";
            default: return "Unknown";
        }
    };

    const columns: ColDef<ServerRequestsFeeInfo>[] = useMemo(() => [
        { 
            field: "operatorName", 
            headerName: "Operator",
            headerTooltip: "Operator",
            flex: 1,
            valueFormatter: (params) => params.value || 'N/A',
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },
        { 
            field: "facilityName", 
            headerName: "Facility",
            headerTooltip: "Facility Name",
            flex: 1,
            valueFormatter: (params) => params.value || 'Operator Fee',
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },
        { 
            field: "feeName", 
            headerName: "Fee Name",
            headerTooltip: "Fee Name",
            flex: 2,
            cellRenderer: (params: any) => {
                const data = params.data as ServerRequestsFeeInfo;
                if (!data) return '';
                
                return (
                    
                    <div style={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        padding: '4px 0'
                    }}>
                        <div style={{ fontWeight: 500, marginBottom: '2px', lineHeight: '1.2' }}>
                            {data.feeName || ''}
                        </div>
                    </div>
                );
            }
        },
       
        { 
            field: "feeScope", 
            headerName: "Scope",
            headerTooltip: "Fee Scope",
            flex: 1,
            valueFormatter: (params) => getFeeScopeText(params.value),
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },
        { 
            field: "feeCalc", 
            headerName: "Calculation",
            headerTooltip: "Fee Calculation Type",
            flex: 1,
            valueFormatter: (params) => getFeeCalcText(params.value),
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },
        { 
            field: "amount", 
            headerName: "Amount",
            headerTooltip: "Fee Amount",
            flex: 1,
            type: "rightAligned",
            valueFormatter: (params) => {
                const data = params.data as ServerRequestsFeeInfo;
                if (!params.value && params.value !== 0) {
                    return 'N/A';
                }
                if (data.feeCalc === 1) {
                    return `${(params.value * 100).toFixed(1)}%`;
                } else {
                    return showMoney(cents(params.value));
                }
            },
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } as any
        },
        { 
            field: "paymentType", 
            headerName: "Payment Type",
            headerTooltip: "Payment Type",
            flex: 1,
            valueFormatter: (params) => getPaymentTypeText(params.value),
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },
        { 
            field: "isAppliedToParker", 
            headerName: "Applied to Parker",
            headerTooltip: "Applied to Parker",
            flex: 1,
            valueFormatter: (params) => params.value ? "Yes" : "No",
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },

        { 
            field: "isOptIn", 
            headerName: "Opt-In",
            headerTooltip: "Is Opt-In",
            flex: 1,
            valueFormatter: (params) => {
                const data = params.data as ServerRequestsFeeInfo;
                if (!data) return '';
                return params.value ? "Yes" : "No";
            },
            cellStyle: { display: 'flex', alignItems: 'center' } as any
        },
     
        {
            headerName: "Actions",
            flex: 1,
            cellRenderer: (params: any) => {
                const data = params.data as ServerRequestsFeeInfo;
                if (!data) return '';
                
                return (
                    <div style={{ 
                        display: 'flex', 
                        gap: '3px', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <button
                            className="btn btn-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFee(data);
                                setIsCloneMode(false);
                                setShowEditModal(true);
                            }}
                            title="Edit Fee"
                        >
                            <Pencil  />
                        </button>
                        <button
                            className="btn btn-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFee(data);
                                setIsCloneMode(true);
                                setShowEditModal(true);
                            }}
                            title="Clone Fee"
                        >
                            <Files />
                        </button>
                        <button
                            className="btn btn-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFee(data);
                                setShowDeleteModal(true);
                            }}
                            title="Delete Fee"
                        >
                            <Trash />
                        </button>
                    </div>
                );
            },
            sortable: false,
            filter: false
        }
    ], []);

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };

    const onRowClicked = (event: RowClickedEvent<ServerRequestsFeeInfo>) => {
        if (event.event?.defaultPrevented) return;
        
        const fee = event.data;
        if (fee) {
            setSelectedFee(fee);
            setShowEditModal(true);
        }
    };

    const fetchFeesData = async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 10));
            console.log("Fetching fees data");

            const response = await feesService.listFees();
            setFeesData(response.data?.items || []);
        } catch (error) {
            console.error("Error loading fees data:", error);
        } finally {
            console.log("Finished loading fees data");
            setShowContent(true);
        }
    };

    const fetchOperators = async () => {
        try {
            // Fetch operators
            const operatorsResponse = await operatorService.list();
            setOperators(operatorsResponse.data?.items || []);
        } catch (error) {
            console.error("Error loading operators:", error);
        }
    };

    const handleDeleteFee = async () => {
        if (!selectedFee?.operatorFeeId) return;
        
        try {
            await feesService.cancelFee({ id: selectedFee.operatorFeeId });
            await fetchFeesData();
        } catch (error) {
            console.error("Error deleting fee:", error);
        } finally {
            setShowDeleteModal(false);
            setSelectedFee(null);
        }
    };

    const handleFeeSaved = (updatedFee: ServerRequestsFeeInfo) => {
        if (selectedFee && !isCloneMode) {
            // Update existing fee in the collection
            setFeesData(prevData => 
                prevData.map(f => 
                    f.operatorFeeId === updatedFee.operatorFeeId ? updatedFee : f
                )
            );
        } else {
            // Add new fee to the collection (for create or clone)
            setFeesData(prevData => [updatedFee, ...prevData]);
        }
        
        setShowEditModal(false);
        setSelectedFee(null);
        setIsCloneMode(false);
    };
    
    // Reset facility selection when operator changes
    useEffect(() => {
        setSelectedFacility("");
    }, [selectedOperator]);

    useEffect(() => {
        fetchFeesData();
        fetchOperators();
    }, []);

    return (
        <CSSTransition
            in={showContent}
            timeout={300}
            classNames="page-slide"
            unmountOnExit
            nodeRef={nodeRef}
        >
            <Container fluid className="h-100" ref={nodeRef}>
                <Row className="h-100">
                    <Col className="h-100" style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr", 
                        gridTemplateRows: "max-content max-content 1fr 5px" 
                    }}>
                        <PageBar title="Operator & Facility Fees">
                            <ToolButton onClick={() => {
                                setSelectedFee(null);
                                setIsCloneMode(false);
                                setShowEditModal(true);
                            }}>
                                <Plus color="maroon" /> Add Fee
                            </ToolButton>
                            <ToolButton onClick={() => setShowTestModal(true)}>
                                <i className="bi bi-calculator me-1"></i> Test Facility
                            </ToolButton>
                        </PageBar>
                        
                        {/* Filter Section */}
                        <div style={{ padding: '12px 0', borderBottom: '1px solid #dee2e6' }}>
                            <Row>
                                <Col md={3}>
                                    <Select
                                        value={selectedOperator}
                                        onChange={(e) => setSelectedOperator(e.target.value)}
                                        label="Filter by Operator"
                                        className="mb-0 text-muted"
                                    >
                                        {operatorOptions.map(option => (
                                            <option key={option.value || 'empty'} value={option.value || ''}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col md={3}>
                                    <Select
                                        value={selectedFacility}
                                        onChange={(e) => setSelectedFacility(e.target.value)}
                                        label="Filter by Facility"
                                        className="mb-0 text-muted"
                                    >
                                        {facilityOptions.map(option => (
                                            <option key={option.value || 'empty'} value={option.value || ''}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                        </div>
                        <div style={{ height: "100%", width: "100%" }}>
                            <DataGrid
                                rowData={filteredFeesData}
                                columnDefs={columns}
                                onGridReady={onGridReady}
                                pagination={true}
                                paginationPageSize={25}
                                paginationPageSizeSelector={[10, 25, 50, 100]}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                    cellStyle: {
                                        display: 'flex',
                                        alignItems: 'center'
                                    }
                                }}
                                rowHeight={60}
                                getRowHeight={() => 60}
                                tooltipShowDelay={200}
                            />
                        </div>
                    </Col>
                </Row>

                <FeeEditModal
                    show={showEditModal}
                    setShow={setShowEditModal}
                    fee={selectedFee}
                    onSaved={handleFeeSaved}
                    operators={operators}
                    facilities={[]}
                    isCloneMode={isCloneMode}
                />

                <TestFacilityModal
                    show={showTestModal}
                    setShow={setShowTestModal}
                    operators={operators}
                />

                <ConfirmModal
                    show={showDeleteModal}
                    setShow={setShowDeleteModal}
                    title="Delete Fee"
                    message={`Are you sure you want to delete the fee "${selectedFee?.feeName}"? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    onConfirm={handleDeleteFee}
                />
            </Container>
        </CSSTransition>
    );
} 